import React, { type FunctionComponent, useEffect, useState, useRef, useCallback } from 'react';
import { useUnmount } from 'react-use';
import { Box } from '@mui/material';

import styles from './ImageSequence.module.scss';

interface ImageSequenceProps {
  imageUrls: string[];
  className?: string;
}

const ImageSequence: FunctionComponent<ImageSequenceProps> = ({ imageUrls, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [completedImages, setCompletedImages] = useState<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);

  // Reset index and preload all images when imageUrls changes
  useEffect(() => {
    if (imageUrls.length === 0) return;

    // Reset to first image when imageUrls array changes
    setCurrentIndex(0);
    setLoadedImages(new Set());
    setCompletedImages(new Set());

    const preloadImages = async () => {
      const promises = imageUrls.map((url, index) => {
        return new Promise<number>((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            setLoadedImages((prev) => new Set(prev).add(index));
            resolve(index);
          };
          img.onerror = () => {
            console.warn(`Failed to load image at index ${index}:`, url);
            reject(new Error(`Failed to load image: ${url}`));
          };
          img.src = url;
          imageRefs.current[index] = img;
        });
      });

      try {
        await Promise.allSettled(promises);
      } catch (error) {
        console.warn('Some images failed to preload:', error);
      }
    };

    preloadImages();
  }, [imageUrls]);

  useEffect(() => {
    if (imageUrls.length <= 1 || loadedImages.size === 0) return;

    const startCycling = () => {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
      }, 1500);
    };

    startCycling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [imageUrls.length, loadedImages.size]);

  useEffect(() => {
    if (imageUrls.length <= 1) return;

    if (currentIndex === 0) {
      setCompletedImages(new Set());
    } else {
      const prevIndex = currentIndex - 1;
      setCompletedImages((prev) => new Set(prev).add(prevIndex));
    }
  }, [currentIndex, imageUrls.length]);

  const getImageStyle = useCallback(
    (index: number) => {
      const isActive = index === currentIndex;
      const isLoaded = loadedImages.has(index);
      const prevIndex = currentIndex - 1;
      const shouldShow = isActive || (index === prevIndex && loadedImages.has(prevIndex));

      return {
        opacity: isActive && isLoaded ? 1 : shouldShow ? 1 : 0,
        transition: isActive && isLoaded ? 'opacity 0.25s ease-in-out' : 'none',
        zIndex: isActive ? 2 : 1,
      };
    },
    [currentIndex, loadedImages],
  );

  useUnmount(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  });

  if (imageUrls.length === 0) {
    return <Box className={`${styles.container} ${className || ''}`} />;
  }

  if (imageUrls.length === 1) {
    return (
      <Box
        className={`${styles.container} ${className || ''}`}
        style={{ backgroundImage: `url(${imageUrls[0]})` }}
      />
    );
  }

  return (
    <Box className={`${styles.container} ${className || ''}`}>
      {imageUrls.map((url, index) => (
        <Box
          key={`image-${index}`}
          className={styles.image}
          style={{
            backgroundImage: `url(${url})`,
            ...getImageStyle(index),
          }}
        />
      ))}
      {imageUrls.length > 1 && (
        <Box className={styles.progressContainer}>
          {imageUrls.map((_, index) => {
            const isActive = index === currentIndex;
            const isCompleted = completedImages.has(index);

            return (
              <Box
                key={`progress-bar-${index}`}
                className={styles.progressBar}
                style={{ width: `${100 / imageUrls.length}%` }}
              >
                <Box
                  className={`${styles.progressFill} ${
                    isActive ? styles.active : isCompleted ? styles.completed : ''
                  }`}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ImageSequence;
